import React from "react";
import {
  Box,
  VStack,
  HStack,
  Button,
  Divider,
  useColorModeValue,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  IconButton,
  useDisclosure,
} from "@chakra-ui/react";
import { HamburgerIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext, useMobile } from "./hooks";
import { signOut } from "./api";
import { ParticleBasketball } from "./ParticleBasketball";
import { getVisiblePrimaryNavItems, isNavItemActive } from "./navigation";

interface NavItemProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const NavItem = ({ label, isActive, onClick }: NavItemProps) => {
  const activeBg = useColorModeValue(
    "brand.50",
    "linear-gradient(90deg, rgba(59, 130, 246, 0.18), rgba(139, 92, 246, 0.12))"
  );
  const activeColor = useColorModeValue("brand.700", "brand.200");
  const hoverBg = useColorModeValue("gray.100", "gray.700");

  return (
    <Button
      variant="ghost"
      justifyContent="flex-start"
      w="100%"
      py={2}
      px={4}
      fontWeight={isActive ? "600" : "400"}
      bg={isActive ? activeBg : "transparent"}
      color={isActive ? activeColor : undefined}
      _hover={{ bg: isActive ? activeBg : hoverBg }}
      borderRadius="md"
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
    >
      {label}
    </Button>
  );
};

const SidebarContent = ({ onClose }: { onClose?: () => void }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAppContext();
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const navItems = getVisiblePrimaryNavItems(user?.isEnterprise);

  const handleNav = (path: string) => {
    navigate(path);
    onClose?.();
  };

  const onSignOut = async () => {
    await signOut();
    setUser(null);
  };

  return (
    <VStack h="100%" align="stretch" spacing={0}>
      <Box
        px={4}
        py={3}
        cursor="pointer"
        onClick={() => window.open("https://www.balldontlie.io")}
        display="flex"
        justifyContent="center"
      >
        <ParticleBasketball height={120} width={145} quality="compact" />
      </Box>

      <Divider borderColor={borderColor} />

      <VStack px={3} py={4} spacing={1} align="stretch" flex={1}>
        {navItems.map((item) => (
          <NavItem
            key={item.path}
            label={item.label}
            isActive={isNavItemActive(location.pathname, item)}
            onClick={() => handleNav(item.path)}
          />
        ))}
      </VStack>

      {!user?.isEnterprise && (
        <>
          <Divider borderColor={borderColor} />

          <VStack px={3} py={3} spacing={1} align="stretch">
            <Button
              variant="ghost"
              justifyContent="flex-start"
              w="100%"
              py={2}
              px={4}
              fontWeight="400"
              onClick={() => window.open("https://lab.balldontlie.io")}
              rightIcon={<ExternalLinkIcon boxSize={3} />}
            >
              BDL Lab
            </Button>
            <Button
              variant="ghost"
              justifyContent="flex-start"
              w="100%"
              py={2}
              px={4}
              fontWeight="400"
              onClick={() => window.open("https://www.storystats.ai")}
              rightIcon={<ExternalLinkIcon boxSize={3} />}
            >
              StoryStats
            </Button>
            <Button
              variant="ghost"
              justifyContent="flex-start"
              w="100%"
              py={2}
              px={4}
              fontWeight="400"
              onClick={() => window.open("https://www.balldontlie.io/docs")}
              rightIcon={<ExternalLinkIcon boxSize={3} />}
            >
              Docs
            </Button>
          </VStack>
        </>
      )}

      <Divider borderColor={borderColor} />

      <Box px={3} py={3}>
        <Button
          variant="ghost"
          justifyContent="flex-start"
          w="100%"
          py={2}
          px={4}
          fontWeight="400"
          onClick={onSignOut}
        >
          Log Out
        </Button>
      </Box>
    </VStack>
  );
};

export const Sidebar = () => {
  const isMobile = useMobile();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const sidebarBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  if (isMobile) {
    return (
      <>
        <HStack
          w="100%"
          p={3}
          justifyContent="space-between"
          borderBottomWidth="1px"
          borderColor={borderColor}
        >
          <Box
            onClick={() => window.open("https://www.balldontlie.io")}
            cursor="pointer"
          >
            <ParticleBasketball height={80} width={96} quality="compact" />
          </Box>
          <IconButton
            aria-label="Open menu"
            icon={<HamburgerIcon />}
            variant="ghost"
            onClick={onOpen}
          />
        </HStack>
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent bg={sidebarBg}>
            <DrawerCloseButton />
            <DrawerBody p={0}>
              <SidebarContent onClose={onClose} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <Box
      w="220px"
      minW="220px"
      h="100vh"
      bg={sidebarBg}
      borderRightWidth="1px"
      borderColor={borderColor}
      position="sticky"
      top={0}
    >
      <SidebarContent />
    </Box>
  );
};
